// src/pages/Updates.js
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";

// Safely convert Firestore / string dates into JS Date objects
function toDateSafe(value) {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    try {
      return value.toDate();
    } catch {}
  }

  if (typeof value === "string" && value.includes("/")) {
    const parts = value.split("/");
    if (parts.length === 3) {
      const [day, month, year] = parts.map((n) => parseInt(n, 10));
      const d = new Date(year, month - 1, day);
      if (!isNaN(d.getTime())) return d;
    }
  }

  if (typeof value === "string") {
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) return parsed;
  }

  return null;
}

function formatUk(date) {
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return "";
  }
}

export default function Updates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "updates"));

        const rows = snap.docs.map((doc) => {
          const data = doc.data() || {};
          const when = toDateSafe(data.date);

          return {
            id: doc.id,
            title: data.title || "Untitled",
            description:
              data.descripton || data.description || "No details provided.",
            dateObj: when,
            dateRaw:
              typeof data.date === "string"
                ? data.date
                : when
                ? formatUk(when)
                : "",
          };
        });

        rows.sort((a, b) => {
          const ta = a.dateObj ? a.dateObj.getTime() : -Infinity;
          const tb = b.dateObj ? b.dateObj.getTime() : -Infinity;
          return tb - ta;
        });

        setItems(rows);
      } catch (e) {
        console.error("Failed to load updates:", e);
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <>
      <Helmet>
        <title>What’s New – ReflectRise</title>
        <meta
          name="description"
          content="Stay up to date with the latest ReflectRise updates, improvements, and new features designed to help you journal smarter and grow stronger."
        />
        <style>{`
          @keyframes fadeInCard {
            0% { opacity: 0; transform: translateY(12px); }
            100% { opacity: 1; transform: translateY(0); }
          }

          /* GOLDEN AMBIENT SHIMMER BACKLIGHT */
          @keyframes shimmerGlow {
            0% { opacity: 0.15; transform: translateY(0px); }
            50% { opacity: 0.32; transform: translateY(-6px); }
            100% { opacity: 0.15; transform: translateY(0px); }
          }

          /* PARTICLE FLOATING ANIMATION */
          @keyframes floatParticle {
            0% { transform: translateY(0) translateX(0); opacity: 0.4; }
            50% { transform: translateY(-18px) translateX(10px); opacity: 0.8; }
            100% { transform: translateY(0) translateX(0); opacity: 0.4; }
          }
        `}</style>
      </Helmet>

      <div
        className="updates-page"
        style={{
          background: "var(--bg)",
          minHeight: "100vh",
          padding: "3rem 1rem",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          className="updates-card"
          style={{
            background: "var(--card)",
            borderRadius: "16px",
            padding: "2rem",
            width: "100%",
            maxWidth: "900px",
            boxShadow: "0 6px 22px rgba(0,0,0,0.45)",
            animation: "fadeInCard 0.5s ease",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* GOLD BACKGLOW BEHIND WHOLE PANEL */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at top, rgba(255,210,85,0.08) 0%, transparent 65%)",
              zIndex: 0,
              pointerEvents: "none",
              animation: "shimmerGlow 6s ease-in-out infinite",
            }}
          ></div>

          <h1
            style={{
              color: "var(--primary)",
              fontSize: "2rem",
              marginBottom: "1rem",
              textAlign: "center",
              position: "relative",
              zIndex: 2,
            }}
          >
            ✨ ReflectRise Updates
          </h1>

          <p
            style={{
              color: "var(--muted)",
              textAlign: "center",
              marginBottom: "2rem",
              lineHeight: "1.6",
              position: "relative",
              zIndex: 2,
            }}
          >
            Stay up to date with every improvement, fix and new feature.
          </p>

          {loading ? (
            <p style={{ textAlign: "center", color: "var(--muted)" }}>
              Loading updates…
            </p>
          ) : items.length === 0 ? (
            <p style={{ textAlign: "center", color: "var(--muted)" }}>
              No updates posted yet.
            </p>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1.2rem",
                position: "relative",
                zIndex: 2,
              }}
            >
              {items.map((u, index) => (
                <div
                  key={u.id}
                  style={{
                    background:
                      "linear-gradient(145deg, var(--card), rgba(255,215,120,0.05))",
                    border: "1px solid rgba(255,215,120,0.15)",
                    borderRadius: "12px",
                    padding: "1.3rem 1.5rem",
                    position: "relative",
                    overflow: "hidden",
                    animation: "fadeInCard 0.5s ease",
                    animationDelay: `${index * 0.08}s`,
                    animationFillMode: "forwards",
                    opacity: 0,
                    transition: "0.3s ease",
                  }}
                >
                  {/* GOLD BACKLIGHT BEHIND EACH CARD */}
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "radial-gradient(circle at center, rgba(255,200,80,0.10), transparent 70%)",
                      animation: "shimmerGlow 7s ease-in-out infinite",
                      opacity: 0.18,
                      pointerEvents: "none",
                      zIndex: 0,
                    }}
                  ></div>

                  {/* FLOATING PARTICLES */}
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      style={{
                        position: "absolute",
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        background: "rgba(255,225,140,0.7)",
                        top: `${20 + i * 15}%`,
                        left: `${10 + i * 18}%`,
                        filter: "blur(2px)",
                        animation: `floatParticle ${4 + i}s ease-in-out infinite`,
                        pointerEvents: "none",
                        zIndex: 0,
                      }}
                    ></div>
                  ))}

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "0.35rem",
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        color: "var(--primary)",
                        fontSize: "1.2rem",
                        position: "relative",
                        zIndex: 2,
                      }}
                    >
                      {u.title}
                    </h3>

                    <span
                      style={{
                        fontSize: "0.9rem",
                        color: "var(--muted)",
                        position: "relative",
                        zIndex: 2,
                      }}
                    >
                      {u.dateObj ? formatUk(u.dateObj) : u.dateRaw}
                    </span>
                  </div>

                  <p
                    style={{
                      margin: 0,
                      color: "var(--text)",
                      opacity: 0.9,
                      lineHeight: "1.5",
                      position: "relative",
                      zIndex: 2,
                    }}
                  >
                    {u.description}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
